import { storage as forgeStorage } from '@forge/api'
import ResolverFromForge from '@forge/resolver'
// Static imports for both Forge and mock dependencies
import {
  Resolver as MockResolver,
  storage as mockStorage,
  webTrigger as mockWebTrigger
} from '../compassPage/src/mocks/forge-api-mock'
import packageJson from '../package.json'
import type {
  BillingInterval,
  DeleteRadarResult,
  Env,
  InvokePayload,
  RadarConfig,
  RadarData,
  RadarDataStorage,
  RadarMetadata,
  RequestContext,
  SubscriptionTier
} from '../types'
import seedConfig from './data/seed-config.json'
import seedDataBackend from './data/seed-data-backend.json' // radar 2
import seedDataFrontend from './data/seed-data-frontend.json'
import { logError, track } from './posthog'
import {
  addItem,
  addRelease,
  checkLicenseLimit,
  deleteItem,
  deleteRelease,
  updateItemProperties,
  updateRelease
} from './radarItemHandler'
// Use DeleteRadarResult from root-level types.ts as the single source of truth
import type {
  AllRadarMetadata,
  SystemSettings,
  UserSettings
} from './schema/zodSchemas'
import {
  AllRadarMetadataSchema,
  CURRENT_SCHEMA_VERSION,
  RadarConfigSchema,
  RadarDataSchema,
  SystemSettingsSchema,
  WebhookSecretSchema
} from './schema/zodSchemas'
import {
  checkStripeSubscription,
  createCheckoutSession,
  createCustomerPortalSession,
  isLicensed
} from './stripeHandler'

export const MAX_UNLICENSED_ITEMS = 10
export const MAX_UNLICENSED_RADARS = 2

const isNotProd = process.env.NODE_ENV !== 'production'
const storage = isNotProd ? mockStorage : forgeStorage
const Resolver = isNotProd ? MockResolver : ResolverFromForge

// biome-ignore lint/suspicious/noExplicitAny: this is for a mock
let webTrigger: any
if (isNotProd) {
  webTrigger = mockWebTrigger
} else {
  // Only import webTrigger in production
  webTrigger = require('@forge/api').webTrigger
}

const STORAGE_KEYS = {
  CONFIG: 'radar_config', //old v0 config
  DATA: 'radar_data', //v1 individual radar data, do not change these
  METADATA: 'all_radars_metadata', //v1 all radar metadata, do not change these
  SYSTEM_SETTINGS: 'system_settings' //v1 system settings including user settings
}

export function getRadarDataKey(radarId: string | number): string {
  return `${STORAGE_KEYS.DATA}_${radarId}`
}

export function createTechradarHandler({
  storage,
  Resolver
}: {
  // biome-ignore lint/suspicious/noExplicitAny: This is used for a mock
  storage: any
  // biome-ignore lint/suspicious/noExplicitAny: This is used for a mock
  Resolver: any
  // biome-ignore lint/suspicious/noExplicitAny: This is used for a mock
  [key: string]: any
}) {
  const resolver = new Resolver()

  const getRadarDataKey = (radarId: string | number): string =>
    `${STORAGE_KEYS.DATA}_${radarId}`

  const getNextRadarId = async (): Promise<number> => {
    const allMetadata = await getAllRadarsMetadata()
    const ids = Object.values(allMetadata.radars)
      .map(r =>
        typeof r.id === 'number' ? r.id : Number.parseInt(String(r.id), 10)
      )
      .filter(id => !Number.isNaN(id))
    return ids.length > 0 ? Math.max(...ids) + 1 : 1
  }

  /**
   * Migrates data from old single-radar storage to new multi-radar storage format
   * This is called if the initial check for `getAllRadarsMetadata` returns no metadata
   */
  const migrateV0ToV1 = async (): Promise<AllRadarMetadata> => {
    const metadata = await storage.get(STORAGE_KEYS.METADATA)
    if (metadata?.schemaVersion === CURRENT_SCHEMA_VERSION) return metadata

    if (!isNotProd) console.log('Metadata not found, performing migration...')

    const oldConfig = await storage.get(STORAGE_KEYS.CONFIG)
    const oldData = await storage.get(STORAGE_KEYS.DATA)

    let allRadarMetadata: AllRadarMetadata
    try {
      if (oldConfig && oldData) {
        // Migrate single radar to new schema as radar 1
        allRadarMetadata = {
          schemaVersion: CURRENT_SCHEMA_VERSION,
          radars: {
            1: {
              id: 1,
              name: oldConfig.name || 'Default Radar',
              config: oldConfig
            }
          }
        }
        const radarDataStorage: RadarDataStorage = {
          schemaVersion: CURRENT_SCHEMA_VERSION,
          data: oldData
        }
        validateAllRadarMetadata(allRadarMetadata)
        await storage.set(STORAGE_KEYS.METADATA, allRadarMetadata)
        await storage.set(getRadarDataKey(1), radarDataStorage)
        // Backup and clean up old keys
        await storage.set('v0_config_backup', oldConfig)
        await storage.set('v0_data_backup', oldData)
        await storage.delete(STORAGE_KEYS.CONFIG)
        await storage.delete(STORAGE_KEYS.DATA)
      } else {
        // No old data: create empty schema
        allRadarMetadata = {
          schemaVersion: CURRENT_SCHEMA_VERSION,
          radars: {}
        }
        validateAllRadarMetadata(allRadarMetadata)
        await storage.set(STORAGE_KEYS.METADATA, allRadarMetadata)
      }
      if (!isNotProd) console.log('Migration completed successfully')
    } catch (error) {
      logError(
        'TechradarHandler',
        'Migration from single radar to multi-radar storage failed:',
        error
      )
      throw error
    }
    return allRadarMetadata
  }

  /**
   * Seeds two default radars for completely new users only.
   * Should be called after migration.
   * Only seeds for new users (no existing radars).
   */
  const seedDefaultRadarsIfMissing = async () => {
    const metadata = (await storage.get(
      STORAGE_KEYS.METADATA
    )) as AllRadarMetadata
    const radars = metadata?.radars || {}

    // Only seed if this is a completely new user (no existing radars)
    if (Object.keys(radars).length > 0) {
      return // User already has radars, don't add seed data
    }

    // Add two default radars for new users
    // Radar 1: Frontend Radar
    radars[1] = {
      id: 1,
      name: 'Frontend Radar',
      config: seedConfig
    }
    const frontendRadarDataStorage: RadarDataStorage = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      data: seedDataFrontend as RadarData
    }
    await storage.set(getRadarDataKey(1), frontendRadarDataStorage)

    // Radar 2: Backend Radar
    radars[2] = {
      id: 2,
      name: 'Backend Radar',
      config: seedConfig
    }
    const backendRadarDataStorage: RadarDataStorage = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      data: seedDataBackend as RadarData
    }
    await storage.set(getRadarDataKey(2), backendRadarDataStorage)

    const newMetadata = {
      ...metadata,
      radars
    }
    validateAllRadarMetadata(newMetadata)
    await storage.set(STORAGE_KEYS.METADATA, newMetadata)
  }

  resolver.define(
    'getVersion',
    ({ context }: { context: RequestContext }): string => {
      track(context as RequestContext, 'admin:version_get')
      return packageJson.version
    }
  )

  resolver.define(
    'getEnv',
    async ({
      context
    }: {
      context: RequestContext
    }): Promise<Env & { userSettings?: UserSettings }> => {
      track(context as RequestContext, 'admin:environment_get')

      // Track user settings if accountId is available
      let userSettings: UserSettings | undefined
      let userEmail: string | null = null

      if (context.accountId) {
        userSettings = await getUserSettings(context.accountId)
        if (userSettings.emailProvided !== false) {
          // Try to fetch from PostHog API
          try {
            let projectId: string | number = ''
            console.log(
              'context.environmentType:',
              context.environmentType,
              context.accountId
            )
            switch (context.environmentType) {
              case 'STAGING':
                projectId = 126623
                break
              case 'PRODUCTION':
                projectId = 126624
                break
              default:
                projectId = 115596
            }
            const apiKey = process.env.POSTHOG_SECRET_KEY
            if (apiKey && context.accountId) {
              const url = `https://app.posthog.com/api/projects/${projectId}/persons?distinct_id=${context.accountId}`
              const resp = await fetch(url, {
                headers: { Authorization: `Bearer ${apiKey}` }
              })
              if (resp.ok) {
                const data = await resp.json()
                if (data?.results && data.results.length > 0) {
                  userEmail = data.results[0].properties?.email || null
                }
              } else {
                // Log error to PostHog
                track(
                  context as RequestContext,
                  'admin:posthog_email_fetch_error',
                  {
                    status: resp.status,
                    statusText: resp.statusText
                  }
                )
              }
            }
          } catch (err) {
            logError(
              'TechradarHandler',
              'Error fetching email from PostHog:',
              err
            )
            // Log error to PostHog
            track(
              context as RequestContext,
              'admin:posthog_email_fetch_error',
              {
                error: err instanceof Error ? err.message : String(err)
              }
            )
          }
        }
      }

      // DO NOT ADD SERVER SECRETS HERE, THIS IS SENT TO THE CLIENT
      return {
        posthog: {
          api_host: 'https://us.i.posthog.com',
          api_key:
            typeof process !== 'undefined' && process.env
              ? process.env.POSTHOG_API_KEY || ''
              : ''
        },
        stripe: {
          perishableKey:
            typeof process !== 'undefined' && process.env
              ? process.env.STRIPE_PERISHABLE_KEY || ''
              : ''
        },
        userSettings,
        userEmail
      }
    }
  )

  // Helper function to get radar metadata
  const getRadarMetadata = async (
    radarId: string | number
  ): Promise<RadarMetadata> => {
    const metadata = await getAllRadarsMetadata()
    const radar = metadata.radars?.[radarId]
    if (!radar) {
      throw new Error(`Radar with ID ${radarId} not found`)
    }
    return radar
  }

  // Helper function to validate AllRadarMetadata
  function validateAllRadarMetadata(data: unknown) {
    const result = AllRadarMetadataSchema.safeParse(data)
    if (!result.success) {
      throw new Error(
        `AllRadarMetadata validation failed: ${JSON.stringify(result.error.format())}`
      )
    }
    return result.data
  }

  // Helper function to validate RadarConfig
  function validateRadarConfig(data: unknown) {
    const result = RadarConfigSchema.safeParse(data)
    if (!result.success) {
      throw new Error(
        `RadarConfig validation failed: ${JSON.stringify(result.error.format())}`
      )
    }
    return result.data
  }

  // Helper function to validate SystemSettings
  function validateSystemSettings(data: unknown) {
    const result = SystemSettingsSchema.safeParse(data)
    if (!result.success) {
      throw new Error(
        `SystemSettings validation failed: ${JSON.stringify(result.error.format())}`
      )
    }
    return result.data
  }

  // Helper function to get system settings
  const getSystemSettings = async (): Promise<SystemSettings> => {
    const settings = await storage.get(STORAGE_KEYS.SYSTEM_SETTINGS)
    if (!settings) {
      // Create default system settings
      const defaultSettings: SystemSettings = {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        users: {}
      }
      await storage.set(STORAGE_KEYS.SYSTEM_SETTINGS, defaultSettings)
      return defaultSettings
    }
    return validateSystemSettings(settings)
  }

  // Helper function to get user settings
  const getUserSettings = async (accountId: string): Promise<UserSettings> => {
    const systemSettings = await getSystemSettings()
    return (
      systemSettings.users[accountId] || {
        emailProvided: false,
        emailPromptDismissed: false
      }
    )
  }

  // Helper function to update user settings
  const updateUserSettings = async (
    accountId: string,
    updates: Partial<UserSettings>
  ): Promise<void> => {
    const systemSettings = await getSystemSettings()
    const currentUserSettings = systemSettings.users[accountId] || {
      emailProvided: false,
      emailPromptDismissed: false
    }

    systemSettings.users[accountId] = {
      ...currentUserSettings,
      ...updates
    }

    await storage.set(STORAGE_KEYS.SYSTEM_SETTINGS, systemSettings)
  }

  // Helper function to get all radars metadata
  const getAllRadarsMetadata = async (): Promise<AllRadarMetadata> => {
    let metadata: AllRadarMetadata | null = await storage.get(
      STORAGE_KEYS.METADATA
    )
    let migrated = false

    if (!metadata || !metadata.schemaVersion) {
      // If no metadata, perform migration
      const result = await migrateV0ToV1()
      // Only treat as migrated if migration did not just return the existing metadata
      if (!metadata || result !== metadata) {
        migrated = true
        metadata = result
      }
    }
    if (migrated) {
      // Only seed default radars if a migration actually occurred
      await seedDefaultRadarsIfMissing()
      // Get updated metadata after seeding
      metadata = await storage.get(STORAGE_KEYS.METADATA)
    }
    if (metadata) validateAllRadarMetadata(metadata)
    // Always return a valid AllRadarMetadata object
    if (!metadata) {
      return {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        radars: {}
      }
    }
    return metadata
  }

  // Helper to ensure mutable arrays for config fields
  function ensureMutableConfigArrays(config: RadarConfig): RadarConfig {
    if (config.sections && Array.isArray(config.sections)) {
      config.sections = Array.from(config.sections)
    }
    if (config.quadrants && Array.isArray(config.quadrants)) {
      config.quadrants = Array.from(config.quadrants)
    }
    if (config.rings && Array.isArray(config.rings)) {
      config.rings = Array.from(config.rings)
    }
    return config
  }

  // Helper function to update radar metadata
  const updateRadarMetadata = async (
    radarId: number,
    updates: Partial<RadarMetadata>
  ): Promise<RadarMetadata> => {
    const allMetadata = await getAllRadarsMetadata()
    const currentMetadata = allMetadata.radars[radarId]

    if (!currentMetadata) {
      throw new Error(`Radar with ID ${radarId} not found`)
    }

    const updatedMetadata: RadarMetadata = {
      ...currentMetadata,
      ...updates
    }

    if (updatedMetadata.config) {
      updatedMetadata.config = ensureMutableConfigArrays(updatedMetadata.config)
    }

    allMetadata.radars[radarId] = updatedMetadata
    validateAllRadarMetadata({
      schemaVersion: CURRENT_SCHEMA_VERSION,
      radars: allMetadata.radars
    })
    allMetadata.schemaVersion = CURRENT_SCHEMA_VERSION
    await storage.set(STORAGE_KEYS.METADATA, allMetadata)

    return updatedMetadata
  }

  resolver.define(
    'getConfig',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarConfig> => {
      const { radarId } = payload || {}
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)

      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      track(context as RequestContext, 'admin:config_get', {
        radar_id: radarId
      })

      const config = ensureMutableConfigArrays(radarMetadata.config)
      return config as RadarConfig
    }
  )

  resolver.define(
    'setConfig',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarConfig> => {
      const { radarId, config } = payload || {}
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)

      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      // Create new config, preserving all fields
      const currentConfig = radarMetadata.config

      const newConfig = {
        ...currentConfig,
        ...config,
        toggles: {
          ...currentConfig.toggles,
          ...config.toggles
        },
        flags: {
          ...currentConfig.flags,
          ...config.flags
        },
        labels: {
          ...currentConfig.labels,
          ...config.labels
        }
      }
      // Validate config before saving
      validateRadarConfig(newConfig)
      // Ensure mutable arrays
      const safeConfig = ensureMutableConfigArrays(newConfig)

      // Update metadata with new config
      await updateRadarMetadata(Number(radarId), {
        id: radarMetadata.id ?? Number(radarId),
        name: radarMetadata.name ?? 'Untitled Radar',
        config: safeConfig
      })

      track(context as RequestContext, 'admin:config_set', {
        radar_id: radarId
      })

      return safeConfig as RadarConfig
    }
  )

  resolver.define(
    'getRadarData',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId } = payload || {}
      const storageKey = getRadarDataKey(radarId)

      // Check if radar exists in metadata
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)
      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      const storageData = (await storage.get(storageKey)) as RadarDataStorage

      const items = storageData?.data?.items || []

      track(context as RequestContext, 'radar:data_get', { radar_id: radarId })

      return { items }
    }
  )

  resolver.define(
    'setRadarData',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, radarData } = payload
      const storageKey = getRadarDataKey(radarId)
      const requestContext = context as RequestContext

      // Check if radar exists in metadata
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)
      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      // validate new data schema
      RadarDataSchema.parse(radarData)

      // Check if this would exceed the unlicensed limit
      if (
        !(await isLicensed(requestContext)) &&
        radarData.items.length > MAX_UNLICENSED_ITEMS
      ) {
        throw new Error(
          `Unlicensed version is limited to ${MAX_UNLICENSED_ITEMS} radar items. Please upgrade to add more items.`
        )
      }

      const newData: RadarDataStorage = {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        data: radarData
      }

      // Update the radar data storage
      await storage.set(storageKey, newData)

      track(requestContext, 'admin:data_update', {
        items_count: radarData.items.length,
        radar_id: radarId
      })

      return newData.data
    }
  )

  // Helper function to check if adding a new item would exceed the limit
  resolver.define(
    'checkLicenseLimit',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<{
      canAdd: boolean
      currentCount: number
      maxItems: number
    }> => {
      const { radarId } = payload || {}
      return checkLicenseLimit(context as RequestContext, radarId)
    }
  )

  resolver.define(
    'updateItemProperties',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, itemId, quadrant, tags, archived } = payload
      return updateItemProperties(
        context as RequestContext,
        itemId,
        quadrant,
        tags,
        archived,
        radarId
      )
    }
  )

  resolver.define(
    'updateRelease',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, itemId, release } = payload
      return updateRelease(context as RequestContext, itemId, release, radarId)
    }
  )

  resolver.define(
    'addRelease',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, itemId, release } = payload
      return addRelease(context as RequestContext, itemId, release, radarId)
    }
  )

  resolver.define(
    'deleteRelease',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, itemId, releaseId } = payload
      return deleteRelease(
        context as RequestContext,
        itemId,
        releaseId,
        radarId
      )
    }
  )

  resolver.define(
    'deleteItem',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, itemId } = payload
      return deleteItem(context as RequestContext, itemId, radarId)
    }
  )

  // Resolver for adding a single item
  resolver.define(
    'addItem',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarData> => {
      const { radarId, item } = payload
      return addItem(context as RequestContext, item, radarId)
    }
  )

  resolver.define(
    'createCheckoutSession',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<{ sessionId: string; url: string }> => {
      const { tier, interval } = payload as {
        tier: SubscriptionTier
        interval: BillingInterval
      }
      const requestContext = context as RequestContext

      return createCheckoutSession(requestContext, tier, interval)
    }
  )

  // Add customer portal session resolver
  resolver.define(
    'createCustomerPortalSession',
    async ({
      context
    }: {
      context: RequestContext
    }): Promise<{ url: string }> => {
      const requestContext = context as RequestContext
      return createCustomerPortalSession(requestContext)
    }
  )

  // Update subscription check resolver to return full status
  resolver.define(
    'checkSubscriptionStatus',
    async ({
      context
    }: {
      context: RequestContext
    }): Promise<{
      isLicensed: boolean
      tier: SubscriptionTier | null
    }> => {
      const requestContext = context as RequestContext
      const status = await checkStripeSubscription(requestContext)

      track(requestContext, 'admin:subscription_check', {
        has_subscription: status.isActive,
        subscription_tier: status.tier
      })

      return {
        isLicensed: status.isActive,
        tier: status.tier
      }
    }
  )

  resolver.define(
    'getRadarsConfig',
    async ({
      context
    }: {
      context: RequestContext
    }): Promise<{ radars: RadarMetadata[] }> => {
      const metadata = await getAllRadarsMetadata()

      const radars = Object.values(metadata.radars)
      track(context as RequestContext, 'admin:radars_list', {
        radars_count: radars.length
      })

      return { radars }
    }
  )

  // Check if user can create more radars
  resolver.define(
    'checkRadarLimit',
    async ({
      context
    }: {
      context: RequestContext
    }): Promise<{
      canCreateRadar: boolean
      currentCount: number
      maxAllowed: number
    }> => {
      const requestContext = context as RequestContext
      const userIsLicensed = await isLicensed(requestContext)

      const metadata = await getAllRadarsMetadata()
      const currentCount = Object.keys(metadata.radars).length

      if (userIsLicensed) {
        return {
          canCreateRadar: true,
          currentCount,
          maxAllowed: -1 // Unlimited for licensed users
        }
      }

      const canCreateRadar = currentCount < MAX_UNLICENSED_RADARS
      return {
        canCreateRadar,
        currentCount,
        maxAllowed: MAX_UNLICENSED_RADARS
      }
    }
  )

  // this returns all radars, with the new radar at the end.
  resolver.define(
    'createRadar',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarMetadata[]> => {
      const { name } = payload
      const requestContext = context as RequestContext

      // Check radar limits for unlicensed users
      const userIsLicensed = await isLicensed(requestContext)
      if (!userIsLicensed) {
        const allMetadata = await getAllRadarsMetadata()
        const currentCount = Object.keys(allMetadata.radars).length

        if (currentCount >= MAX_UNLICENSED_RADARS) {
          throw new Error(
            `Free tier is limited to ${MAX_UNLICENSED_RADARS} radars. Please upgrade to create more radars.`
          )
        }
      }

      const radarId = await getNextRadarId()
      const allMetadata = await getAllRadarsMetadata()

      const newRadarMetadata: RadarMetadata = {
        id: radarId,
        name,
        config: ensureMutableConfigArrays({ ...seedConfig })
      }

      allMetadata.radars[radarId] = newRadarMetadata

      // validate radar metadata schema
      validateAllRadarMetadata(allMetadata)

      await storage.set(STORAGE_KEYS.METADATA, allMetadata)

      // When creating empty radar data, always include schemaVersion
      const emptyRadarData: RadarDataStorage = {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        data: { items: [] }
      }

      // validate empty radar data schema
      RadarDataSchema.parse(emptyRadarData.data)

      await storage.set(getRadarDataKey(radarId), emptyRadarData)

      track(requestContext, 'admin:radar_create', {
        radar_id: radarId,
        radar_name: name
      })

      return Object.values(allMetadata.radars)
    }
  )

  resolver.define(
    'renameRadar',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarMetadata> => {
      const { radarId, newName } = payload
      const requestContext = context as RequestContext

      // Update the radar metadata with the new name
      const updatedMetadata = await updateRadarMetadata(radarId, {
        name: newName
      })

      track(requestContext, 'admin:radar_rename', {
        radar_id: radarId,
        radar_name: newName
      })

      return updatedMetadata
    }
  )

  // ADDED: updateRadar resolver for frontend compatibility
  resolver.define(
    'updateRadar',
    async ({
      payload,
      context
    }: {
      payload: { radarId: number; name: string }
      context: RequestContext
    }): Promise<RadarMetadata> => {
      try {
        const { radarId, name } = payload
        const updatedMetadata = await updateRadarMetadata(radarId, { name })
        track(context, 'admin:radar_update', {
          radar_id: radarId,
          radar_name: name
        })
        return updatedMetadata
      } catch (error) {
        // Use the same error handler pattern as elsewhere in this file
        logError(
          'TechradarHandler',
          '[updateRadar] Error updating radar:',
          error
        )
        throw new Error('Failed to update radar name.')
      }
    }
  )

  resolver.define(
    'deleteRadar',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<DeleteRadarResult> => {
      const { radarId } = payload
      const requestContext = context as RequestContext

      // Check if radar exists
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)
      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      // Check if this is the last radar - this is the ONLY restriction
      const allMetadata = await getAllRadarsMetadata()
      const radarCount = Object.keys(allMetadata.radars).length
      if (radarCount <= 1) {
        return {
          success: false,
          error: 'Cannot delete the last radar. At least one radar must exist.'
        }
      }

      // Proceed with deletion
      delete allMetadata.radars[radarId]
      await storage.set(STORAGE_KEYS.METADATA, allMetadata)

      // Delete the radar data storage
      await storage.delete(getRadarDataKey(radarId))

      track(requestContext, 'admin:radar_delete', {
        radar_id: radarId
      })

      return { success: true }
    }
  )

  // Add export/import functions with schema version support
  resolver.define(
    'exportRadarConfig',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<{ config: RadarConfig; schemaVersion: number }> => {
      const { radarId } = payload || {}
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)

      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      track(context as RequestContext, 'admin:config_export', {
        radar_id: radarId
      })

      const config = ensureMutableConfigArrays(radarMetadata.config)
      return {
        config: config as RadarConfig,
        schemaVersion: 1
      }
    }
  )

  resolver.define(
    'importRadarConfig',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<RadarConfig> => {
      const { radarId, config, schemaVersion = 0 } = payload || {}
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)

      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      const safeConfig = ensureMutableConfigArrays(config)

      // Update metadata with imported config
      await updateRadarMetadata(Number(radarId), {
        id: radarMetadata.id ?? Number(radarId),
        name: radarMetadata.name ?? 'Untitled Radar',
        config: safeConfig
      })

      track(context as RequestContext, 'admin:config_import', {
        radar_id: radarId,
        from_schema_version: schemaVersion,
        to_schema_version: CURRENT_SCHEMA_VERSION
      })

      return safeConfig as RadarConfig
    }
  )

  // Delete all items for a radar
  resolver.define(
    'deleteAllItemsForRadar',
    async ({
      payload,
      context
    }: {
      payload: InvokePayload
      context: RequestContext
    }): Promise<{
      success: boolean
    }> => {
      const { radarId } = payload
      const requestContext = context as RequestContext

      // Check if radar exists
      const radarMetadata: RadarMetadata | null =
        await getRadarMetadata(radarId)
      if (!radarMetadata) {
        throw new Error(`Radar with ID ${radarId} not found`)
      }

      // Overwrite radar data with empty items array
      const storageKey = getRadarDataKey(radarId)
      const storageData = (await storage.get(storageKey)) as RadarDataStorage
      const currentData = storageData?.data || {
        items: []
      }

      const newData: RadarData = {
        ...currentData,
        items: []
      }

      await storage.set(storageKey, {
        schemaVersion: storageData?.schemaVersion || 1,
        data: newData
      })

      // Optionally track this event
      track(requestContext, 'admin:items_delete_all', { radar_id: radarId })

      return { success: true }
    }
  )

  // Export all radars with schemaVersion
  resolver.define(
    'exportAllRadars',
    async (): Promise<{
      schemaVersion: number
      radars: RadarMetadata[]
      data: Record<number, RadarDataStorage>
    }> => {
      const allMetadata = await getAllRadarsMetadata()
      const radars: RadarMetadata[] = Object.values(allMetadata.radars)
      const data: Record<number, RadarDataStorage> = {}

      for (const radar of radars) {
        data[radar.id] = await storage.get(getRadarDataKey(radar.id))
      }

      const schemaVersion = CURRENT_SCHEMA_VERSION

      try {
        AllRadarMetadataSchema.parse({
          schemaVersion,
          radars: allMetadata.radars
        })
        for (const d of Object.values(data)) {
          RadarDataSchema.parse(d.data)
        }
      } catch (err) {
        throw new Error(`Export validation failed: ${JSON.stringify(err)}`)
      }

      return {
        schemaVersion,
        radars,
        data
      }
    }
  )

  // Import all radars with migration
  resolver.define(
    'importAllRadars',
    async ({
      payload
    }: {
      payload: InvokePayload
    }): Promise<{ success: boolean; error?: string }> => {
      const { schemaVersion, radars, data } = payload

      if (!schemaVersion || typeof schemaVersion !== 'number') {
        return { success: false, error: 'Missing or invalid schemaVersion' }
      }
      if (!Array.isArray(radars) || typeof data !== 'object') {
        return { success: false, error: 'Invalid radars or data format' }
      }

      try {
        AllRadarMetadataSchema.parse({
          schemaVersion,
          radars: Object.fromEntries(
            radars.map((r: RadarMetadata) => [r.id, r])
          )
        })
        for (const d of Object.values(data)) {
          RadarDataSchema.parse((d as RadarDataStorage).data)
        }
      } catch (err) {
        return {
          success: false,
          error: `Import validation failed: ${JSON.stringify(err)}`
        }
      }

      const metadata: Record<number, RadarMetadata> = {}
      for (const radar of radars as RadarMetadata[]) {
        const radarId = Number(radar.id)
        if (Number.isNaN(radarId)) {
          return { success: false, error: 'Radar ID must be a valid number' }
        }
        radar.id = radarId
        if (radar.config) {
          radar.config = ensureMutableConfigArrays(radar.config)
        }
        metadata[radar.id] = radar
      }
      await storage.set(STORAGE_KEYS.METADATA, metadata)

      for (const [radarIdStr, radarData] of Object.entries(data)) {
        const radarId = Number(radarIdStr)
        if (Number.isNaN(radarId)) continue
        const radarDataObj = radarData as RadarDataStorage
        await storage.set(getRadarDataKey(radarId), {
          schemaVersion,
          data: radarDataObj.data
        })
      }

      return { success: true }
    }
  )

  resolver.define(
    'setWebhookSecret',
    async ({
      payload
    }: {
      payload: { secret: string }
      context: RequestContext
    }) => {
      try {
        WebhookSecretSchema.parse(payload.secret)
        await storage.set('webhook_secret', payload.secret)
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : String(error)
        }
      }
    }
  )

  // Resolver to get the webhook secret from storage
  resolver.define('getWebhookSecret', async () => {
    const secret = await storage.get('webhook_secret')
    return { secret }
  })

  // --- NEW: Webhook URL resolvers ---
  resolver.define('getWebhookUrl', async () => {
    return await webTrigger.getUrl('techradarWebTrigger')
  })

  resolver.define('rotateWebhook', async (): Promise<string> => {
    const url = await webTrigger.getUrl('techradarWebTrigger')
    await webTrigger.deleteUrl(url)
    return await webTrigger.getUrl('techradarWebTrigger', true)
  })

  // User settings resolvers
  resolver.define(
    'updateUserSettings',
    async ({
      payload,
      context
    }: {
      payload: { updates: Partial<UserSettings> }
      context: RequestContext
    }): Promise<UserSettings> => {
      const { updates } = payload
      const requestContext = context as RequestContext

      if (!requestContext.accountId) {
        throw new Error('Account ID is required to update user settings')
      }

      await updateUserSettings(requestContext.accountId, updates)

      track(requestContext, 'admin:user_settings_update', {
        account_id: requestContext.accountId,
        updates: Object.keys(updates).join(',')
      })

      return getUserSettings(requestContext.accountId)
    }
  )

  resolver.define(
    'submitEmail',
    async ({
      payload,
      context
    }: {
      payload: { email: string }
      context: RequestContext
    }): Promise<{ success: boolean }> => {
      const { email } = payload
      const requestContext = context as RequestContext

      if (!requestContext.accountId) {
        throw new Error('Account ID is required to submit email')
      }

      // If email is empty, treat as clear operation
      if (!email) {
        await updateUserSettings(requestContext.accountId, {
          emailProvided: false,
          lastEmailPromptDate: Date.now()
        })
        try {
          track(requestContext, 'admin:email_cleared', {
            account_id: requestContext.accountId,
            site_url: requestContext.siteUrl
          })
          return { success: true }
        } catch (error) {
          logError('TechradarHandler', 'Error tracking email clear:', error)
          return { success: true }
        }
      }

      // Validate email format only if not empty
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        throw new Error('Invalid email format')
      }

      // Update user settings to mark email as provided
      await updateUserSettings(requestContext.accountId, {
        emailProvided: true,
        emailPromptDismissed: false, // Reset this since they provided email
        lastEmailPromptDate: Date.now()
      })

      try {
        // Track event, but email is more relevant on the frontend
        track(requestContext, 'admin:email_provided', {
          account_id: requestContext.accountId,
          email: email,
          site_url: requestContext.siteUrl
        })

        return { success: true }
      } catch (error) {
        logError(
          'TechradarHandler',
          'Error sending email to analytics systems:',
          error
        )
        // Still mark as provided even if analytics fail
        return { success: true }
      }
    }
  )

  return {
    resolver,
    getDefinitions: () =>
      typeof resolver.handlers === 'object' ? resolver.handlers : resolver,
    migrateV0ToV1,
    getRadarMetadata,
    getAllRadarsMetadata,
    updateRadarMetadata
  }
}

export const techradarHandler = createTechradarHandler({ storage, Resolver })
export const techradarResolver = techradarHandler.resolver.getDefinitions()
export const migrateV0ToV1 = techradarHandler.migrateV0ToV1
export const getRadarMetadata = techradarHandler.getRadarMetadata
export const getAllRadarsMetadata = techradarHandler.getAllRadarsMetadata
export const updateRadarMetadata = techradarHandler.updateRadarMetadata
